"use client";

import { c as _c } from "react/compiler-runtime";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Link as MuiLink, Paper, Stack, Typography, useTheme } from "@mui/material";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import { MOBILE_PHASE_NAV_HEIGHT } from "#/features/playground/ui/MobilePhaseNavBar";
import { useI18nContext } from "#/shared/hooks";
import { useMobileLayout } from "#/shared/hooks/useMobileLayout";
import { glassOverlaySx } from "#/shared/ui/styles/glassOverlayStyles";
type CookieConsentBannerProps = {
  isSettingsView: boolean;
  onAcceptAll: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onRejectNonEssential: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onClose: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Visual surface used by optional dismiss effects (e.g. DOM disintegrate). */
  surfaceRef?: React.Ref<HTMLDivElement>;
};
export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = t0 => {
  const $ = _c(64);
  const {
    isSettingsView,
    onAcceptAll,
    onRejectNonEssential,
    onClose,
    surfaceRef
  } = t0;
  const {
    LL
  } = useI18nContext();
  const theme = useTheme();
  const router = useRouter();
  const isMobileLayout = useMobileLayout();
  const isPlaygroundRoute = router.pathname.startsWith("/playground");
  const reserveMobileNavSpace = isMobileLayout && isPlaygroundRoute;
  const t1 = reserveMobileNavSpace ? `calc(${MOBILE_PHASE_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))` : 0;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      xs: 2,
      sm: 3
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = reserveMobileNavSpace ? 2 : "calc(16px + env(safe-area-inset-bottom, 0px))";
  let t4;
  if ($[1] !== t1 || $[2] !== t3) {
    t4 = {
      position: "fixed",
      bottom: t1,
      left: 0,
      right: 0,
      zIndex: 1200,
      px: t2,
      pb: t3,
      pt: 2,
      pointerEvents: "none"
    };
    $[1] = t1;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      xs: 2,
      sm: 3
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== theme) {
    t6 = glassOverlaySx(theme);
    $[5] = theme;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = {
      pointerEvents: "auto",
      maxWidth: 960,
      mx: "auto",
      px: t5,
      py: 2,
      borderRadius: 2,
      position: "relative",
      ...t6
    };
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== LL || $[10] !== isSettingsView || $[11] !== onClose) {
    t8 = isSettingsView ? <IconButton aria-label={LL.COOKIE_SETTINGS_CLOSE()} onClick={onClose} size="small" sx={{
      position: "absolute",
      top: 8,
      right: 8
    }}><CloseIcon fontSize="small" /></IconButton> : null;
    $[9] = LL;
    $[10] = isSettingsView;
    $[11] = onClose;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      xs: "column",
      sm: "row"
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      alignItems: {
        xs: "stretch",
        sm: "center"
      },
      justifyContent: "space-between"
    };
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = isSettingsView ? 5 : 0;
  let t12;
  if ($[15] !== t11) {
    t12 = {
      minWidth: 0,
      pr: t11
    };
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      mb: 0.5
    };
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== LL || $[19] !== isSettingsView) {
    t14 = isSettingsView ? LL.COOKIE_SETTINGS_TITLE() : LL.COOKIE_CONSENT_TITLE();
    $[18] = LL;
    $[19] = isSettingsView;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t14) {
    t15 = <Typography id="cookie-consent-title" variant="subtitle2" component="h2" sx={t13}>{t14}</Typography>;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== LL || $[24] !== isSettingsView) {
    t16 = isSettingsView ? LL.COOKIE_SETTINGS_MESSAGE() : LL.COOKIE_CONSENT_MESSAGE();
    $[23] = LL;
    $[24] = isSettingsView;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      fontWeight: 600
    };
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== LL) {
    t18 = LL.PRIVACY_POLICY();
    $[27] = LL;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t18) {
    t19 = <MuiLink component={Link} href="/privacy" underline="hover" color="inherit" sx={t17}>{t18}</MuiLink>;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t16 || $[32] !== t19) {
    t20 = <Typography id="cookie-consent-description" variant="body2" color="text.secondary">{t16}{" "}{t19}.</Typography>;
    $[31] = t16;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== t12 || $[35] !== t15 || $[36] !== t20) {
    t21 = <Box sx={t12}>{t15}{t20}</Box>;
    $[34] = t12;
    $[35] = t15;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      xs: "column",
      sm: "row"
    };
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      flexShrink: 0
    };
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== LL) {
    t24 = LL.COOKIE_REJECT_NON_ESSENTIAL();
    $[40] = LL;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== onRejectNonEssential || $[43] !== t24) {
    t25 = <Button variant="outlined" color="primary" onClick={onRejectNonEssential}>{t24}</Button>;
    $[42] = onRejectNonEssential;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== LL) {
    t26 = LL.COOKIE_ACCEPT_ALL();
    $[45] = LL;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== onAcceptAll || $[48] !== t26) {
    t27 = <Button variant="outlined" color="primary" onClick={onAcceptAll}>{t26}</Button>;
    $[47] = onAcceptAll;
    $[48] = t26;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== t25 || $[51] !== t27) {
    t28 = <Stack direction={t22} spacing={1} sx={t23}>{t25}{t27}</Stack>;
    $[50] = t25;
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== t21 || $[54] !== t28) {
    t29 = <Stack direction={t9} spacing={2} sx={t10}>{t21}{t28}</Stack>;
    $[53] = t21;
    $[54] = t28;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== surfaceRef || $[57] !== t29 || $[58] !== t7 || $[59] !== t8) {
    t30 = <Paper ref={surfaceRef} elevation={0} sx={t7}>{t8}{t29}</Paper>;
    $[56] = surfaceRef;
    $[57] = t29;
    $[58] = t7;
    $[59] = t8;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== t30 || $[62] !== t4) {
    t31 = <Box component="section" role="region" aria-labelledby="cookie-consent-title" aria-describedby="cookie-consent-description" sx={t4}>{t30}</Box>;
    $[61] = t30;
    $[62] = t4;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  return t31;
};